import apiClient from './api';

export interface AvatarModel {
  id: string;
  name: string;
  preview_image_url: string;
  gender?: 'male' | 'female' | 'other';
  provider: 'd_id' | 'heygen';
  source_type: 'stock' | 'custom' | 'uploaded';
  source?: 'heygen_stock' | 'private_upload';
  metadata?: Record<string, any>;
}

export interface AvatarLook {
  id: string;
  name: string;
  preview_image_url?: string;
  preview_video_url?: string;
  avatar_type?: string;
  gender?: string;
}

export interface AvatarVoice {
  voice_id: string;
  name: string;
  language: string;
  gender: 'male' | 'female' | 'other';
  preview_audio_url?: string;
  provider: 'd_id' | 'heygen';
}

interface AvatarModelsResponse {
  success: boolean;
  data: {
    avatars: AvatarModel[];
    total: number;
    configured_providers: {
      d_id: boolean;
      heygen: boolean;
    };
  };
}

interface AvatarVoicesResponse {
  success: boolean;
  data: {
    voices: AvatarVoice[];
    total: number;
  };
}

export const avatarModelsService = {
  /**
   * List available avatar models from configured providers
   */
  async listAvatarModels(
    provider?: 'd_id' | 'heygen',
    options?: {
      limit?: number;
      token?: string;
      search?: string;
      gender?: 'male' | 'female' | 'other';
      source?: 'heygen_stock' | 'private_upload';
    }
  ): Promise<{
    avatars: AvatarModel[];
    next_token: string | null;
    has_more: boolean;
    configured_providers: { d_id: boolean; heygen: boolean };
  }> {
    try {
      const qs = new URLSearchParams();
      if (provider) qs.set('provider', provider);
      if (options?.limit) qs.set('limit', String(options.limit));
      if (options?.token) qs.set('token', options.token);
      if (options?.search) qs.set('search', options.search);
      if (options?.gender) qs.set('gender', options.gender);
      if (options?.source) qs.set('source', options.source);
      const params = qs.toString() ? `?${qs.toString()}` : '';
      const response = await apiClient.get<any>(`/api/avatar-models${params}`);
      const data = response.data?.data || {};
      return {
        avatars: data.avatars || [],
        next_token: data.next_token || null,
        has_more: !!data.has_more,
        configured_providers: data.configured_providers || { d_id: false, heygen: false },
      };
    } catch (error) {
      console.error('Failed to fetch avatar models:', error);
      return {
        avatars: [],
        next_token: null,
        has_more: false,
        configured_providers: { d_id: false, heygen: false },
      };
    }
  },

  /**
   * Upload a photo and register it as a private HeyGen Photo Avatar.
   * Sync — typically <10s end-to-end.
   */
  async uploadPrivateAvatar(input: {
    file: File;
    name: string;
    gender?: 'male' | 'female' | 'other';
    consent_acknowledged: boolean;
    /** Optional — when set, the photo is added as another look in this existing group */
    avatar_group_id?: string;
  }): Promise<{ success: boolean; avatar?: AvatarModel & { supported_api_engines: string[] }; error?: string }> {
    try {
      const form = new FormData();
      form.append('file', input.file);
      form.append('name', input.name);
      if (input.gender) form.append('gender', input.gender);
      form.append('consent_acknowledged', input.consent_acknowledged ? 'true' : 'false');
      if (input.avatar_group_id) form.append('avatar_group_id', input.avatar_group_id);

      const response = await apiClient.post<any>('/api/avatar-models/upload-photo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const body = response.data;
      if (body?.success === false || body?.error) {
        return { success: false, error: body?.error?.error_description || body?.error?.message || 'Upload failed' };
      }
      return { success: true, avatar: body?.data?.avatar };
    } catch (error: any) {
      const msg = error?.response?.data?.error?.error_description
        || error?.response?.data?.message
        || error?.message
        || 'Upload failed';
      console.error('Failed to upload private avatar:', error);
      return { success: false, error: msg };
    }
  },

  /**
   * Update a private avatar's display name and/or gender.
   * Cache-only — HeyGen doesn't expose photo-avatar rename via API.
   */
  async updatePrivateAvatar(
    avatar_id: string,
    updates: { name?: string; gender?: 'male' | 'female' | 'other' }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await apiClient.patch<any>(
        `/api/avatar-models/private/${encodeURIComponent(avatar_id)}`,
        updates,
      );
      if (response.data?.error) {
        return { success: false, error: response.data.error.error_description || 'Update failed' };
      }
      return { success: true };
    } catch (error: any) {
      const msg = error?.response?.data?.error?.error_description || error?.message || 'Update failed';
      console.error('Failed to update private avatar:', error);
      return { success: false, error: msg };
    }
  },

  /**
   * Delete a private avatar.
   * Tries HeyGen-side delete first (best-effort) and always removes from our library.
   */
  async deletePrivateAvatar(avatar_id: string): Promise<{
    success: boolean;
    error?: string;
    deleted_on_heygen?: boolean;
    description?: string;
  }> {
    try {
      const response = await apiClient.delete<any>(
        `/api/avatar-models/private/${encodeURIComponent(avatar_id)}`,
      );
      if (response.data?.error) {
        return { success: false, error: response.data.error.error_description || 'Delete failed' };
      }
      return {
        success: true,
        deleted_on_heygen: !!response.data?.data?.deleted_on_heygen,
        description: response.data?.description,
      };
    } catch (error: any) {
      const msg = error?.response?.data?.error?.error_description || error?.message || 'Delete failed';
      console.error('Failed to delete private avatar:', error);
      return { success: false, error: msg };
    }
  },

  /**
   * Manually trigger a HeyGen avatar cache refresh for the current tenant
   */
  async refreshAvatarCache(): Promise<{
    upserted: number;
    deactivated: number;
    pages_fetched: number;
    skipped_reason?: string;
    description?: string;
  } | null> {
    try {
      const response = await apiClient.post<any>('/api/avatar-models/refresh');
      const body = response.data || {};
      return {
        ...(body.data || {}),
        description: body.description,
      };
    } catch (error) {
      console.error('Failed to refresh avatar cache:', error);
      return null;
    }
  },

  /**
   * List all looks (variations) for a specific HeyGen avatar group
   */
  async listLooksForGroup(group_id: string): Promise<AvatarLook[]> {
    try {
      const response = await apiClient.get<any>(`/api/avatar-models/looks/${group_id}`);
      return response.data?.data?.looks || [];
    } catch (error) {
      console.error('Failed to fetch avatar looks:', error);
      return [];
    }
  },

  /**
   * List available HeyGen voices for full_animated_video mode
   */
  async listVoices(language?: string): Promise<AvatarVoice[]> {
    try {
      const params = language ? `?language=${encodeURIComponent(language)}` : '';
      const response = await apiClient.get<AvatarVoicesResponse>(
        `/api/avatar-models/voices${params}`
      );
      return response.data?.data?.voices || [];
    } catch (error) {
      console.error('Failed to fetch avatar voices:', error);
      return [];
    }
  },
};
